import { useAllLists } from 'state/lists/hooks'
import { getVersionUpgrade, VersionUpgrade } from '@uniswap/token-lists'
import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { UNSUPPORTED_LIST_URLS } from 'config/constants/lists'
import useWeb3Provider from 'hooks/useActiveWeb3React'
import useFetchListCallback from 'hooks/useFetchListCallback'
import { AppDispatch } from '../index'
import { acceptListUpdate } from './actions'
import { useActiveListUrls } from './hooks'

export default function Updater(): null {
  const { library } = useWeb3Provider()
  const dispatch = useDispatch<AppDispatch>()

  // get all loaded lists, and the active urls
  const lists = useAllLists()
  const activeListUrls = useActiveListUrls()

  const fetchList = useFetchListCallback()
  
  // Track which lists we've checked for updates in this session (prevents duplicate checks)
  const updateCheckRef = useRef<Set<string>>(new Set())
  
  // ON-DEMAND fetching with update checking:
  // - When app loads: Fetches if not loaded OR checks for updates if already loaded (once per session)
  // - When user manually adds/refreshes a list
  // - No automatic polling - only on-demand

  // On-demand fetch: Loads list if not loaded, OR checks for updates if already loaded
  // This ensures fresh data and updated token lists
  useEffect(() => {
    if (!library) return // Wait for Web3 library to initialize
    
    // Debounce to prevent rapid-fire requests
    const timeoutId = setTimeout(() => {
      Object.keys(lists).forEach((listUrl) => {
        const list = lists[listUrl]
        
        // Skip if currently loading or has recent error
        if (list.loadingRequestId || list.error) return
        
        // Case 1: List not loaded yet - fetch it (initial load)
        if (!list.current) {
          fetchList(listUrl).catch((error) => {
            // Only log non-connection errors to avoid noise
            if (!error.message?.includes('ECONNRESET') && !error.message?.includes('aborted')) {
              console.debug('list added fetching error', error)
            }
          })
          return
        }
        
        // Case 2: List already loaded - check for updates (on-demand, once per session)
        // Fetch fresh data to compare versions and detect updates
        // This ensures users get updated token lists when they return to the app
        if (list.current && !updateCheckRef.current.has(listUrl)) {
          updateCheckRef.current.add(listUrl)
          
          // Fetch fresh data to check for version updates
          // The reducer will:
          // 1. Compare current version with fetched version
          // 2. If different: Set pendingUpdate with new list
          // 3. Updater will automatically accept all updates (PATCH, MINOR, MAJOR)
          fetchList(listUrl).catch((error) => {
            // Only log non-connection errors to avoid noise
            if (!error.message?.includes('ECONNRESET') && !error.message?.includes('aborted')) {
              console.debug('update check error', error)
            }
          })
        }
      })
    }, 100) // Small delay to batch requests
    
    return () => clearTimeout(timeoutId)
  }, [dispatch, fetchList, library, lists])

  // if any lists from unsupported lists are loaded, check them too (in case new updates since last visit)
  useEffect(() => {
    Object.keys(UNSUPPORTED_LIST_URLS).forEach((listUrl) => {
      const list = lists[listUrl]
      if (!list || (!list.current && !list.loadingRequestId && !list.error)) {
        fetchList(listUrl).catch((error) => console.debug('list added fetching error', error))
      }
    })
  }, [dispatch, fetchList, library, lists])

  // automatically update lists if versions are minor/patch
  useEffect(() => {
    Object.keys(lists).forEach((listUrl) => {
      const list = lists[listUrl]
      if (list.current && list.pendingUpdate) {
        try {
          const bump = getVersionUpgrade(list.current.version, list.pendingUpdate.version)
          // eslint-disable-next-line default-case
          switch (bump) {
            case VersionUpgrade.NONE:
              // This shouldn't happen, but don't crash - just log and skip
              console.warn(`[Token List Updater] ⚠️ Unexpected no version bump for ${listUrl}, skipping update`)
              return
            // update any active or inactive lists
            case VersionUpgrade.PATCH:
            case VersionUpgrade.MINOR:
            case VersionUpgrade.MAJOR:
              dispatch(acceptListUpdate(listUrl))
          }
        } catch (error) {
          // Don't crash on update errors - log and continue
          console.error(`[Token List Updater] ❌ Error updating list ${listUrl}:`, error)
          // App continues with existing token list
        }
      }
    })
  }, [dispatch, lists, activeListUrls])

  return null
}
